import { useEffect, useState } from 'react';

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState<{
    width?: number;
    height?: number;
  }>({ width: undefined, height: undefined });

  useEffect(() => {
    const handleResize = () =>
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

export const useEscape = (onEscape: any) => {
  useEffect(() => {
    const handleEsc = (event: any) => {
      if (event.keyCode === 27) onEscape();
    };

    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onEscape]);
};

export const useDeviceDetect = () => {
  const userAgent =
    typeof navigator === 'undefined' ? 'SSR' : navigator.userAgent;

  const detectDevice = (userAgent: string) => {
    const isAndroid = () => Boolean(userAgent.match(/Android/i));
    const isIos = () => Boolean(userAgent.match(/iPhone|iPad|iPod/i));
    const isSSR = () => Boolean(userAgent.match(/SSR/i));

    const isMobile = () => Boolean(isAndroid() || isIos());
    const isDesktop = () => Boolean(!isMobile() && !isSSR());

    return {
      isAndroid,
      isIos,
      isSSR,
      isMobile,
      isDesktop,
    };
  };

  return detectDevice(userAgent);
};

export const useOutsideClick = (ref: any, callback: any) => {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target)) {
        callback();
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [ref, callback]);
};

/**
 * Returns the current device pixel ratio (DPR).
 */
export const getDevicePixelRatio = (defaultDpr = 1): number => {
  const hasDprProp =
    typeof window !== 'undefined' &&
    typeof window.devicePixelRatio === 'number';
  const dpr = hasDprProp ? window.devicePixelRatio : defaultDpr;

  return Math.round(dpr * 100) / 100;
};

/**
 * Returns the current device pixel ratio (DPR).
 * Emits new value on change.
 */
export const useDevicePixelRatio = (defaultDpr = 1): number => {
  const [currentDpr, setCurrentDpr] = useState(getDevicePixelRatio(defaultDpr));

  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return;

    const updateDpr = () => setCurrentDpr(getDevicePixelRatio(defaultDpr));
    const mediaMatcher = window.matchMedia(
      `screen and (resolution: ${currentDpr}dppx)`,
    );

    updateDpr();
    mediaMatcher.addEventListener('change', updateDpr);

    return () => mediaMatcher.removeEventListener('change', updateDpr);
  }, [currentDpr, defaultDpr]);

  return currentDpr;
};
